import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { PrimeArtifact } from '../types/primeArtifact';
import { PrimeModelCreationData } from '../types/primeModel';

interface PrimeModelContextType {
  selectedArtifact: PrimeArtifact | null;
  modelData: PrimeModelCreationData | null;
  selectArtifact: (artifact: PrimeArtifact) => void;
  updateModelData: (data: PrimeModelCreationData) => void;
  completeModel: () => void;
  resetModel: () => void;
  saveSelectedArtifact: () => void;
  restoreSelectedArtifact: () => void;
}

const PrimeModelContext = createContext<PrimeModelContextType | undefined>(undefined);

export const PrimeModelProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedArtifact, setSelectedArtifact] = useState<PrimeArtifact | null>(null);
  const [modelData, setModelData] = useState<PrimeModelCreationData | null>(null);
  const [savedArtifact, setSavedArtifact] = useState<PrimeArtifact | null>(null);
  const [savedArtifactName, setSavedArtifactName] = useState<string | null>(null);
  const isRestoringArtifact = useRef(false);

  // Removed verbose mount/unmount logs

  const selectArtifact = useCallback((artifact: PrimeArtifact) => {
    // Removed verbose selectArtifact log
    setSelectedArtifact(artifact);
    setModelData({
      artifact,
      states: artifact.states || [],
      transitions: artifact.transitions || [],
      processes: artifact.processes || [],
    });
  }, []);

  const updateModelData = useCallback(
    (data: PrimeModelCreationData) => {
      console.log('[PrimeModelContext] updateModelData called:', {
        artifactId: data.artifact.id,
        artifactName: data.artifact.name,
        statesCount: data.states.length,
        transitionsCount: data.transitions.length,
        processesCount: data.processes.length,
        currentModelDataId: modelData?.artifact?.id,
      });

      setModelData((prev) => {
        if (!prev) {
          console.log('[PrimeModelContext] No previous modelData, creating new');
          return data;
        }
        if (prev.artifact.id !== data.artifact.id) {
          console.log('[PrimeModelContext] Different artifact, updating modelData');
          return data;
        }
        console.log('[PrimeModelContext] Same artifact, updating modelData');
        return {
          ...prev,
          states: data.states,
          transitions: data.transitions,
          processes: data.processes,
        };
      });
    },
    [modelData?.artifact?.id]
  );

  const completeModel = useCallback(() => {
    console.log('[PrimeModelContext] completeModel called');
    // TODO: Implement model completion logic
  }, []);

  const resetModel = useCallback(() => {
    console.log('[PrimeModelContext] resetModel called');
    setSelectedArtifact(null);
    setModelData(null);
  }, []);

  const saveSelectedArtifact = useCallback(() => {
    console.log('[PrimeModelContext] saveSelectedArtifact called:', {
      id: selectedArtifact?.id,
      name: selectedArtifact?.name,
    });
    setSavedArtifact(selectedArtifact);
    setSavedArtifactName(selectedArtifact?.name || null);
  }, [selectedArtifact]);

  const restoreSelectedArtifact = useCallback(() => {
    if (isRestoringArtifact.current) return;

    console.log('[PrimeModelContext] restoreSelectedArtifact called:', {
      id: savedArtifact?.id,
      name: savedArtifact?.name,
      savedArtifactName,
    });

    isRestoringArtifact.current = true;

    try {
      if (savedArtifactName && modelData?.artifact) {
        // Try to find the artifact by name in the current model data
        const artifactToSelect =
          modelData.artifact.name === savedArtifactName ? modelData.artifact : null;

        if (artifactToSelect) {
          console.log('[PrimeModelContext] Found artifact by name:', artifactToSelect.name);
          setSelectedArtifact(artifactToSelect);
        } else if (savedArtifact) {
          // Fallback to the saved artifact if we can't find it by name
          console.log('[PrimeModelContext] Using saved artifact as fallback:', savedArtifact.name);
          setSelectedArtifact(savedArtifact);
        }
      } else if (savedArtifact) {
        // If we don't have a name but have a saved artifact, use that
        console.log(
          '[PrimeModelContext] Using saved artifact (no name available):',
          savedArtifact.name
        );
        setSelectedArtifact(savedArtifact);
      }
    } finally {
      isRestoringArtifact.current = false;
    }
  }, [savedArtifact, savedArtifactName, modelData?.artifact]);

  const value = useMemo(
    () => ({
      selectedArtifact,
      modelData,
      selectArtifact,
      updateModelData,
      completeModel,
      resetModel,
      saveSelectedArtifact,
      restoreSelectedArtifact,
    }),
    [
      selectedArtifact,
      modelData,
      selectArtifact,
      updateModelData,
      completeModel,
      resetModel,
      saveSelectedArtifact,
      restoreSelectedArtifact,
    ]
  );

  return <PrimeModelContext.Provider value={value}>{children}</PrimeModelContext.Provider>;
};

export const usePrimeModel = () => {
  const context = useContext(PrimeModelContext);
  if (context === undefined) {
    throw new Error('usePrimeModel must be used within a PrimeModelProvider');
  }
  return context;
};
